import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const fetchingRef = useRef(false);

  const isAuthenticated = !!user;

  const buildUser = (authUser, profile) => ({
    id: authUser.id,
    email: authUser.email,
    name: profile?.name || authUser.user_metadata?.full_name || '',
    plan: profile?.plan || 'Free',
    currency: profile?.currency || 'CLP',
    initials: profile?.initials || (authUser.user_metadata?.full_name || authUser.email || '').slice(0, 2).toUpperCase(),
    photoUrl: profile?.photo_url || authUser.user_metadata?.avatar_url || null,
    coupleId: profile?.couple_id || null,
    migratedFromLocal: profile?.migrated_from_local || false,
  });

  const fetchProfile = useCallback(async (authUser) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authUser.id)
        .single();

      if (error) console.warn('Profile fetch error:', error.message);
      setUser(buildUser(authUser, profile));
    } catch (err) {
      console.warn('fetchProfile exception:', err);
      setUser(buildUser(authUser, null));
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  // Initialize: get session + listen for changes
  useEffect(() => {
    if (!isSupabaseConfigured()) {
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        fetchProfile(session.user);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_OUT') {
          setUser(null);
          setLoading(false);
        }
        // TOKEN_REFRESHED — update silently if user already exists
        if (event === 'TOKEN_REFRESHED' && session?.user) {
          fetchProfile(session.user);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  const loginWithGoogle = useCallback(async () => {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
    if (error) throw error;
  }, []);

  const loginWithEmail = useCallback(async (email, password) => {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    if (data?.user) await fetchProfile(data.user);
  }, [fetchProfile]);

  const signUpWithEmail = useCallback(async (email, password, name) => {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: name } },
    });
    if (error) throw error;
    if (data?.user) await fetchProfile(data.user);
  }, [fetchProfile]);

  const logout = useCallback(async () => {
    if (!isSupabaseConfigured()) return;
    await supabase.auth.signOut();
    setUser(null);
  }, []);

  const updateProfile = useCallback(async (data) => {
    if (!isSupabaseConfigured() || !user) return;
    const dbData = {};
    if (data.name !== undefined) dbData.name = data.name;
    if (data.email !== undefined) dbData.email = data.email;
    if (data.plan !== undefined) dbData.plan = data.plan;
    if (data.currency !== undefined) dbData.currency = data.currency;
    if (data.photoUrl !== undefined) dbData.photo_url = data.photoUrl;
    if (data.initials !== undefined) dbData.initials = data.initials;
    if (data.migratedFromLocal !== undefined) dbData.migrated_from_local = data.migratedFromLocal;

    const { error } = await supabase.from('profiles').update(dbData).eq('id', user.id);
    if (error) throw error;
    setUser((prev) => ({ ...prev, ...data }));
  }, [user]);

  const refreshProfile = useCallback(async () => {
    if (!isSupabaseConfigured()) return;
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (authUser) await fetchProfile(authUser);
  }, [fetchProfile]);

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      loading,
      loginWithGoogle,
      loginWithEmail,
      signUpWithEmail,
      logout,
      updateProfile,
      refreshProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
